import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * MagicLinkLanding — handles `/auth/magic?token=...`. Pulls the token
 * out of the query string, posts to the backend's consume endpoint,
 * and bounces home on success. Token is single-use; if the user clicks
 * the link twice, the 2nd click shows the "already used" message.
 */
export default function MagicLinkLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { consumeMagicLink } = useAuth();
  const [error, setError] = useState(null);
  const token = params.get("token");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError("This sign-in link is missing a token.");
      return;
    }
    const run = async () => {
      const res = await consumeMagicLink(token);
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "This sign-in link is invalid or expired.");
        return;
      }
      toast.success("Signed in.");
      navigate("/", { replace: true });
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="magic-link-landing">
      <div className="max-w-sm w-full text-center">
        {error ? (
          <>
            <h1 className="font-display font-bold text-lg">Hmm, that didn't work</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              className="mt-4 h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold"
              data-testid="magic-link-error-back"
            >
              Back to TruckTrackr
            </button>
          </>
        ) : (
          <>
            <div className="inline-block animate-spin rounded-full border-2 border-muted border-t-primary h-7 w-7" />
            <p className="mt-3 text-sm text-muted-foreground">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
